import { useEffect, useRef, type MutableRefObject } from "react";

import type { ChapterBoard } from "@/lib/timeline/board";
import { sampleBoard } from "@/lib/timeline/board";
import type { FitCastBoardContext } from "@/lib/timeline/board/FitCast/context";
import type { PlaybackState } from "@/lib/timeline/playback";
import type { TimelineFrameDetail } from "@/lib/timeline/scene/TimelineFrame/frameDetail";

export interface TimelineFrameLoopOptions {
  onTick: (deltaMs: number) => void;
  getPlayback: () => PlaybackState;
  /** Runs after tick, before `timeline-frame` (e.g. camera follow + CSS). */
  beforeFrame?: () => void;
  /** When set, dispatches one `sampleBoard` per frame on the event detail. */
  getBoardFrame?: () => {
    board: ChapterBoard;
    viewportWidth: number;
  } | null;
  /** Updated each frame before `beforeFrame` (camera fit uses the same sample). */
  boardFitRef?: MutableRefObject<FitCastBoardContext | null>;
}

export function useTimelineFrameLoop(
  canvas: HTMLCanvasElement | null,
  options: TimelineFrameLoopOptions,
): void {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (canvas === null) {
      return;
    }

    let frameId = 0;
    let lastTime = performance.now();

    const frame = (): void => {
      frameId = requestAnimationFrame(frame);
      const now = performance.now();
      const deltaMs = now - lastTime;
      lastTime = now;

      const { onTick, getPlayback, beforeFrame, getBoardFrame, boardFitRef } =
        optionsRef.current;
      onTick(deltaMs);
      const playback = getPlayback();
      const boardFrame = getBoardFrame?.() ?? null;
      const detail: TimelineFrameDetail = { playback };
      if (boardFrame !== null) {
        const sample = sampleBoard(boardFrame.board, playback.playheadMs);
        detail.boardSample = sample;
        detail.viewportWidth = boardFrame.viewportWidth;
        if (boardFitRef !== undefined) {
          boardFitRef.current = {
            board: boardFrame.board,
            sample,
          };
        }
      } else if (boardFitRef !== undefined) {
        boardFitRef.current = null;
      }
      beforeFrame?.();
      canvas.dispatchEvent(
        new CustomEvent("timeline-frame", {
          detail,
        }),
      );
    };

    frameId = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [canvas]);
}
